<script>
import Badge from '@/components/Badge'
import Tags from '@/components/Tags'
import Referrers from '@/components/Lead/Referrers'
import API from '@/api/Lead'
import { LeadPathByModel } from '@/components/Lead/utils'

export default {
  props: {
    lead: { type: Object, required: true },
  },
  computed: {
    view() {
      const { ownership, scopes, isArchived } = this.lead

      if (ownership.length) return 'owner'
      if (isArchived) return 'archived'
      if (!scopes.length) return 'lost'

      return 'lead'
    },

    style() {
      switch (this.view) {
        case 'owner':
          return { '--bg': '#EBDFD1' }
        case 'archived':
        case 'lost':
          return { '--bg': '#E5E6E7' }
        default:
          return { '--bg': '#D6E4EB' }
      }
    },
    reference() {
      return this.lead.reference
    },
    tagsUrl() {
      return `/api/${LeadPathByModel[this.reference.onModel]}/${this.reference.id}/lead/tags`
    },
    scopes() {
      let scopes = this.lead.scopes
      if (this.view === 'owner')
        scopes = scopes.filter(s => s.onModel !== 'Project' && !this.lead.ownership.find(o => o.ref === s.ref))

      return scopes.map(scope => {
        const LinkMap = {
          Project: { name: 'project-dashboard', params: { id: scope.ref } },
          Page: { name: 'page-dashboard', params: { id: scope.ref } },
          Residential: { name: 'residential-edit', params: { id: scope.ref } },
          ResidentialTemplate: { name: 'residential-template-dashboard', params: { id: scope.ref } },
        }

        return {
          link: LinkMap[scope.onModel],
          label: scope.name || this.$t('theWholeProject'),
        }
      })
    },
    coowners() {
      const { ownership, contact } = this.lead

      return ownership.flatMap(unit =>
        unit.owners
          .filter(({ id, leads }) => id !== contact.id && leads.length)
          .map(owner => ({ ...owner, unit, lead: owner.leads.at(0).id })),
      )
    },
  },
  methods: {
    setTags(tags) {
      API.setTags(tags, this.lead.id)
    },
  },
  components: {
    Badge,
    Tags,
    Referrers,
  },
}
</script>

<template>
  <div :class="['lead-hero', 'lead-hero--view-' + view]" :style="style">
    <div class="lead-hero__header container">
      <slot name="header"></slot>
    </div>

    <div class="lead-hero__summary">
      <div data-part="title">
        <Badge v-if="lead.demo || lead.contact.demo" theme="warning">{{ $t('demo') }}</Badge>
        <Badge v-if="view === 'archived'" icon="fa-pro-light:box-archive">{{ $t('archived') }}</Badge>
        <Badge v-if="lead.reference.onModel !== 'Page' && view === 'lost'" icon="fa-pro-light:heart-broken">{{
          $t('notInterestedAnymore')
        }}</Badge>

        <div data-part="title-content">
          <h1>{{ lead.contact.name }}</h1>
          <ButtonComponent
            tag="router-link"
            :to="{
              name: 'contact-edit',
              params: { id: $path('contact.id', lead) },
              query: { returnTo: $route.fullPath, current: lead.reference.id },
            }"
            :label="$t('edit')"
            :icon="['fal', 'pen']"
            class="btn"
          />
        </div>
      </div>

      <div data-part="properties">
        <div data-part="property">
          <Icon icon="fa-pro-light:envelope" />
          <a :href="`mailto:${lead.contact.email}`">{{ lead.contact.email }}</a>
        </div>
        <div v-if="lead.contact.phone" data-part="property">
          <Icon icon="fa-pro-light:phone" />
          <a :href="`tel:${lead.contact.phone}`">{{ lead.contact.phone }}</a>
        </div>
        <div data-part="property">
          <div data-part="property">
            <Icon icon="fa-pro-light:bullseye" />
            <span>{{ $tc('leadReferrer', lead.referrer.length) }}:</span>
          </div>
          <Referrers :value="lead.referrer">
            <template #fallback>-</template>
          </Referrers>
        </div>
        <div data-part="property">
          <div data-part="property">
            <Icon icon="fa-pro-light:tags" />
            <span>{{ $tc('tag', lead.tags.length) }}:</span>
          </div>
          <Tags
            v-model="lead.tags"
            @input="setTags"
            :ignored="$store.state.enums.SystemTags"
            :add-label="lead.tags.length ? null : $tc('addTags', 1)"
            :items="tagsUrl"
          />
        </div>
      </div>

      <div v-if="view === 'owner'" data-part="relations">
        <div v-if="lead.ownership.length" data-part="relation">
          <div data-part="property">
            <Icon icon="fa-pro-light:key" />
            <span>{{ $t('hasPurchased') }}:</span>
          </div>
          <router-link
            v-for="item in lead.ownership"
            :to="{ name: 'residential-edit', params: { id: item.ref } }"
            data-part="relation-item"
          >
            <span>{{ item.name }}</span>
            <Icon icon="fa-pro-light:external-link" />
          </router-link>
        </div>

        <div v-if="view === 'owner' && coowners.length" data-part="relation">
          <div data-part="property">
            <Icon icon="fa-pro-light:people" />
            <span>{{ $t('bidCoApplicant') }}:</span>
          </div>
          <router-link
            v-for="item in coowners"
            :to="{ name: 'project-lead', params: { id: item.lead, reference: reference.id } }"
            data-part="relation-item"
          >
            <span
              >{{ item.name }} <template v-if="lead.ownership.length > 1">({{ item.unit.name }})</template></span
            >
            <Icon icon="fa-pro-light:external-link" />
          </router-link>
        </div>
      </div>
      <div v-if="scopes.length" data-part="relation">
        <div data-part="property">
          <Icon icon="fa-pro-light:heart" />
          <span>{{ $t('isInterestedIn') }}:</span>
        </div>

        <router-link v-for="item in scopes" :to="item.link" data-part="relation-item">
          <span>{{ item.label }}</span>
          <Icon icon="fa-pro-light:external-link" />
        </router-link>
      </div>
    </div>
    <div class="lead-hero__reference" :style="{ backgroundImage: `url(/api/lead/${lead.id}/cover)` }"></div>
  </div>
</template>

<style lang="scss">
.lead-hero {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, 1fr);
  grid-template-areas: 'header header' 'summary reference';

  padding-top: 0.75rem;
  padding-bottom: 6rem;

  background-color: var(--bg);

  &--view-archived {
    @include Stripes(var(--k-ui-color-neutral-lightest), css-lighten('k-ui-color-neutral-light', 1%), -35deg, 3rem);
  }

  h1 {
    line-height: 1;
    margin: 0;
  }

  &__header {
    grid-area: header;

    margin-bottom: 0;
  }

  &__summary {
    grid-area: summary;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 1rem;

    padding-left: var(--container-outer-spacing);

    --max: 0px;
    margin-left: #{'max(var(--max), (100vw - var(--container-limit)) / 2)'};
  }

  &__reference {
    grid-area: reference;
    flex-shrink: 0;

    overflow: clip;
    border-top-left-radius: 1rem;
    border-bottom-left-radius: 1rem;

    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;

    min-height: 40vh;
  }

  &__actions {
    display: contents;
  }

  [data-part='title'] {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    .elder-button {
      font-size: 0.8em;
      border-radius: 999px;
      --vue-elder-button-padding-x: 1rem;
      --vue-elder-button-padding-y: 0.25rem;
    }
  }

  [data-part='title-content'] {
    display: flex;
    gap: 0.5rem;
  }

  [data-part='properties'] {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  [data-part='relations'] {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
  }

  [data-part='property'] {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    a {
      text-decoration: none;
    }
  }

  [data-part='relation'] {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &:has([data-part='relation-item']:nth-child(6)) {
      display: block;
      columns: 2;
      column-gap: 1rem;

      [data-part='property'] {
        column-span: all;
        margin-bottom: 0.25rem;
      }
    }
  }

  [data-part='relation-item'] {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
